<template>
  <v-layout column>
    <v-dialog
      v-model="dialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="800"
      persistent
      @keydown.esc="dialogOpened = false"
    >
      <v-card block flat class="px-4">
        <v-card-title class="pb-0">
          <span class="title" v-if="selectedVocab">Edit term</span>
          <span class="title" v-else>Add new term</span>
          <v-spacer />
          <span class="caption error--text">*Required</span>
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="showDuplicateAlert"
            type="error"
            :value="true"
            class="text-xs-left"
            outline
          >
            Term <b>{{ duplicateWords }}</b> already exists in the glossary
          </v-alert>
          <v-form ref="form1" v-model="vocabValid">
            <v-layout column>
              <v-flex xs12 class="pr-2">
                <div class="text-xs-left">
                  <span class="error--text">*</span>
                  Keyword
                </div>
                <v-text-field
                  name="words"
                  type="text"
                  v-model="words"
                  placeholder="e.g. Proprietorship"
                  required
                  solo
                  class="default-input"
                />
              </v-flex>
              <v-flex xs12 class="pr-2">
                <div class="text-xs-left">
                  <span class="error--text">*</span>
                  Description
                </div>
                <quill-editor
                  v-model="description"
                  :options="editorOption"
                  class="description-input default-input"
                />
              </v-flex>
            </v-layout>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="primary text-none"
            @click="submitVocab()"
            :loading="submitting"
            :disabled="formDisabled"
          >
            Save
          </v-btn>
          <v-btn
            class="error--text text-none"
            flat
            @click="dialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-layout align-center wrap>
      <div class="font-weight-bold mx-2">Search</div>
      <v-flex sm4>
        <v-text-field
          v-model="searchCriteria"
          class="default-input subheading"
          solo
          placeholder="Search by keyword or description"
          hide-details
        />
      </v-flex>
      <v-spacer />
      <soraban-action-button
        icon="post_add"
        help="Add new"
        id="invite-firm-users-btn"
        @click="openCreateDialog"
      />
      <soraban-action-button
        @click="openHelpCenter"
        icon="help"
        help="More info"
        id="glossary-info-btn"
      />
    </v-layout>
    <v-data-table
      :headers="headers"
      :items="glossary"
      :loading="loading"
      :search="searchCriteria"
    >
      <template v-slot:items="props">
        <td class="text-xs-left">
          <b>{{ props.item.key_words }}</b>
        </td>
        <td
          class="text-xs-left text-highlight-box"
          v-html="props.item.description"
        />
        <td>
          <v-layout align-center class="ml-2">
            <v-menu
              transition="slide-y-transition"
              bottom
              nudge-bottom="30"
              class="my-1"
            >
              <v-icon slot="activator" color="primary"> more_vert </v-icon>
              <v-list>
                <v-list-tile style="cursor: pointer">
                  <v-list-tile-title @click="editVocab(props.item)">
                    Edit
                  </v-list-tile-title>
                </v-list-tile>
                <v-list-tile style="cursor: pointer">
                  <v-list-tile-title @click="deleteVocab(props.item.id)">
                    Remove
                  </v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
          </v-layout>
        </td>
      </template>
      <template v-slot:no-results>
        Your search for "{{ searchCriteria }}" found no results.
      </template>
    </v-data-table>
  </v-layout>
</template>

<script>
import API from '@/api';

export default {
  name: 'FirmGlossaryTab',
  props: {
    firmId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      vocabValid: false,
      showDuplicateAlert: false,
      dialogOpened: false,
      searchCriteria: '',
      glossary: [],
      words: null,
      duplicateWords: null,
      description: null,
      loading: false,
      submitting: false,
      selectedVocab: null,
      headers: [
        {
          text: 'Keyword',
          align: 'left',
          value: 'key_words',
        },
        { text: 'Description', value: 'description' },
        {
          text: 'Actions',
          value: '',
          sortable: false,
          width: '100px',
          class: 'text-xs-center',
        },
      ],
      editorOption: {
        formats: ['bold', 'italic', 'underline', 'link', 'indent', 'list'],
        modules: {
          toolbar:
            window.innerWidth >= 768
              ? [
                  ['bold', 'italic', 'underline'],
                  [{ indent: '-1' }, { indent: '+1' }],
                  ['link'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                ]
              : [['bold', 'italic', 'underline']],
        },
        placeholder:
          'e.g. A business that is owned and controlled by one person',
      },
    };
  },
  computed: {
    formDisabled() {
      return !this.words || !this.description;
    },
  },
  methods: {
    async loadGlossary() {
      this.loading = true;
      const vocabs = await API.firm.getFirmVocabs(this.firmId);
      this.glossary = vocabs.data;
      this.loading = false;
    },
    openHelpCenter() {
      window.open('https://helpcenter.soraban.com/firm-glossary', '_blank');
    },
    openCreateDialog() {
      this.selectedVocab = null;
      this.words = null;
      this.description = null;
      this.dialogOpened = true;
      this.showDuplicateAlert = false;
    },
    async submitVocab() {
      this.submitting = true;
      this.showDuplicateAlert = false;

      const exists = this.glossary.find(
        glossary =>
          glossary.key_words.toLowerCase() === this.words.toLowerCase(),
      );
      if (
        (!this.selectedVocab && exists) ||
        (this.selectedVocab &&
          this.selectedVocab.key_words.toLowerCase() !==
            this.words.toLowerCase() &&
          exists)
      ) {
        this.duplicateWords = this.words;
        this.showDuplicateAlert = true;
      } else {
        if (this.selectedVocab) {
          await API.firm.updateGlossaryVocab({
            firmId: this.firmId,
            id: this.selectedVocab.id,
            key_words: this.words,
            description: this.description,
          });
        } else {
          await API.firm.createGlossaryVocab({
            firmId: this.firmId,
            key_words: this.words,
            description: this.description,
          });
        }
        this.loadGlossary();
        this.dialogOpened = false;
      }

      this.submitting = false;
    },
    async editVocab(selected) {
      this.selectedVocab = selected;
      this.words = selected.key_words;
      this.description = selected.description;
      this.dialogOpened = true;
    },
    async deleteVocab(id) {
      await API.firm.deleteGlossaryVocab({
        firmId: this.firmId,
        id,
      });
      this.loadGlossary();
    },
  },
  created() {
    this.loadGlossary();
  },
};
</script>

<style>
.description-input .ql-editor {
  min-height: 80px;
}

.text-highlight-box p {
  margin: 0;
}
.text-highlight-box a {
  color: rgba(0, 0, 0, 0.87);
}
</style>
